<script setup lang="ts">
/**
 * AppHeader — постоянная навигация магазина на ВСЕХ страницах (моб + десктоп).
 * Лого → каталог, адрес, РАБОЧИЙ поиск (синхронизирован с ?q= каталога), корзина
 * с суммой/счётчиком, на десктопе — пункты «Заказы/Профиль». На страницах-деталях
 * (товар/заказ) — кнопка «Назад». Поиск фильтрует каталог (HomePage читает ?q=).
 */
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import VdIcon from "./VdIcon.vue";
import AssistantLaunch from "./AssistantLaunch.vue";
import { useCartStore } from "../stores/cart";
import { formatKopecks } from "../utils/money";
import { brand } from "../brand";
import { useAuthStore } from "../stores/auth";
import { useOutletStore } from "../stores/outlet";
import ThemeToggle from "./ThemeToggle.vue";

const route = useRoute();
const router = useRouter();
const cart = useCartStore();
const auth = useAuthStore();
const outlet = useOutletStore();

// Точка доставки: показываем выбранную (адрес + ETA), иначе — дефолт бренда (пока точки грузятся
// или справочник пуст). Клик по адресу открывает выбор точки.
const outletPickerOpen = ref(false);
const displayAddress = computed(() => outlet.selectedOutlet?.address ?? brand.address);
const displayEta = computed(() => outlet.selectedOutlet?.eta ?? brand.addressEta);
function chooseOutlet(code: string): void {
  outlet.select(code);
  outletPickerOpen.value = false;
}

const q = ref(typeof route.query.q === "string" ? route.query.q : "");
watch(
  () => route.query.q,
  (v) => { q.value = typeof v === "string" ? v : ""; },
);
let debounce: ReturnType<typeof setTimeout> | undefined;
function pushQuery(): void {
  const query = q.value.trim();
  void router.push({ name: "home", query: query ? { q: query } : {} });
}
function onInput(): void {
  clearTimeout(debounce);
  debounce = setTimeout(pushQuery, 250);
}
function onSubmit(): void {
  clearTimeout(debounce);
  pushQuery();
}

const showBack = computed(
  () => route.name === "product" || route.name === "order" || route.name === "checkout",
);
function back(): void {
  if (window.history.length > 1) router.back();
  else void router.push("/");
}
const cartCount = computed(() => cart.itemsCount);
// New visitors fill in the marketer-configured CDP form; current customers keep their profile route.
// Гость → единый экран входа (=вступление в лояльность), а не отдельный лендинг-дубль. Авторизованный → профиль.
const profileTarget = computed(() => (auth.isAuthed ? "/profile" : "/auth"));
const linkClass = "rounded-lg px-3 py-2 text-sm font-medium text-vd-muted hover:bg-vd-tint hover:text-vd-ink";
const linkActive = "text-vd-ink bg-vd-tint";
</script>

<template>
  <header class="sticky top-0 z-40 border-b border-vd-line bg-vd-card">
    <div class="vd-container">
      <!-- Ряд 1 -->
      <div class="flex h-14 items-center gap-2 md:gap-4">
        <button v-if="showBack" class="vd-iconbtn-neutral -ml-1 md:hidden" aria-label="Назад" @click="back">
          <VdIcon name="arrow-left" :size="20" />
        </button>
        <RouterLink to="/" class="flex shrink-0 items-center gap-2" aria-label="Каталог">
          <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-vd-brand text-white"><VdIcon name="building-store" :size="18" /></span>
          <span class="text-sm font-semibold text-vd-ink sm:text-[18px]">{{ brand.name }}</span>
        </RouterLink>
        <button class="hidden shrink-0 items-center gap-1 text-sm text-vd-ink lg:flex" type="button" aria-label="Выбрать точку доставки" @click="outletPickerOpen = true">
          <VdIcon name="map-pin" :size="16" class="text-vd-brand" /> {{ displayAddress }} <span class="text-vd-muted">· {{ displayEta }}</span>
          <VdIcon name="chevron-down" :size="14" class="text-vd-muted" />
        </button>

        <!-- Поиск (десктоп, в строку) -->
        <form class="hidden min-w-0 flex-1 md:block" role="search" @submit.prevent="onSubmit">
          <label class="flex items-center gap-2 rounded-[14px] bg-vd-tint px-3.5 py-2.5">
            <VdIcon name="search" :size="18" class="shrink-0 text-vd-muted" />
            <input v-model="q" type="search" enterkeyhint="search" :placeholder="brand.searchPlaceholder" class="w-full bg-transparent text-[16px] text-vd-ink outline-none placeholder:text-vd-muted" @input="onInput" />
          </label>
        </form>

        <!-- Навигация (десктоп) -->
        <nav class="hidden shrink-0 items-center gap-0.5 md:flex">
          <RouterLink to="/recipes" :class="[linkClass, route.path.startsWith('/recipes') ? linkActive : '']">Рецепты</RouterLink>
          <AssistantLaunch nav />
          <RouterLink to="/orders" :class="[linkClass, route.path.startsWith('/orders') ? linkActive : '']">Заказы</RouterLink>
          <RouterLink :to="profileTarget" :class="[linkClass, (route.path.startsWith('/profile') || route.path.startsWith('/loyalty')) ? linkActive : '']">Профиль</RouterLink>
        </nav>

        <ThemeToggle />

        <!-- Корзина -->
        <RouterLink to="/cart" class="relative ml-auto flex shrink-0 items-center gap-2 rounded-[14px] bg-vd-brand px-3 py-2 text-white md:ml-0">
          <VdIcon name="shopping-cart" :size="18" />
          <span class="text-sm font-medium">{{ formatKopecks(cart.totalKopecks) }}</span>
          <span v-if="cartCount > 0" class="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger-500 px-1 text-[12px] font-semibold leading-none text-white ring-2 ring-vd-card">{{ cartCount }}</span>
        </RouterLink>
      </div>

      <!-- Ряд 2 (моб): адрес + поиск -->
      <div class="md:hidden">
        <button class="flex items-center gap-1 pb-1.5 text-[15px] text-vd-ink" type="button" aria-label="Выбрать точку доставки" @click="outletPickerOpen = true">
          <VdIcon name="map-pin" :size="15" class="text-vd-brand" /> {{ displayAddress }} <span class="text-vd-muted">· {{ displayEta }}</span>
          <VdIcon name="chevron-down" :size="14" class="text-vd-muted" />
        </button>
        <form class="pb-2.5" role="search" @submit.prevent="onSubmit">
          <label class="flex items-center gap-2 rounded-[14px] bg-vd-tint px-3.5 py-2.5">
            <VdIcon name="search" :size="18" class="shrink-0 text-vd-muted" />
            <input v-model="q" type="search" enterkeyhint="search" :placeholder="brand.searchPlaceholder" class="w-full bg-transparent text-[16px] text-vd-ink outline-none placeholder:text-vd-muted" @input="onInput" />
          </label>
        </form>
      </div>
    </div>

    <!-- Выбор точки доставки. Открывается кликом по адресу (десктоп + моб). На мобиле — лист снизу,
         на десктопе — центрированная модалка. Выбор сохраняется (localStorage) и уходит в чекаут. -->
    <div
      v-if="outletPickerOpen"
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center"
      @click.self="outletPickerOpen = false"
    >
      <div class="w-full max-w-md rounded-t-2xl bg-vd-card p-4 shadow-sticky md:rounded-2xl" role="dialog" aria-modal="true" aria-label="Точка доставки">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-[17px] font-semibold text-vd-ink">Точка доставки</h2>
          <button class="vd-iconbtn-neutral" type="button" aria-label="Закрыть" @click="outletPickerOpen = false">
            <VdIcon name="x" :size="20" />
          </button>
        </div>
        <button
          type="button"
          class="mb-2 flex w-full items-center justify-center gap-2 rounded-[14px] bg-vd-tint px-3 py-2.5 text-sm font-medium text-vd-brand disabled:opacity-60"
          :disabled="outlet.locating"
          @click="outlet.selectNearest()"
        >
          <VdIcon name="target" :size="16" /> {{ outlet.locating ? "Определяем…" : "Ближайшая ко мне" }}
        </button>
        <ul class="scrollbar-hidden max-h-[60vh] space-y-1 overflow-y-auto">
          <li v-for="o in outlet.outlets" :key="o.code">
            <button
              type="button"
              class="flex w-full items-start gap-2 rounded-[14px] px-3 py-2.5 text-left hover:bg-vd-tint"
              :class="o.code === outlet.selectedCode ? 'bg-vd-tint' : ''"
              @click="chooseOutlet(o.code)"
            >
              <VdIcon name="map-pin" :size="18" class="mt-0.5 shrink-0 text-vd-brand" />
              <span class="min-w-0 flex-1">
                <span class="block text-[15px] font-medium text-vd-ink">{{ o.name }}</span>
                <span class="block text-[13px] text-vd-muted">{{ o.address }} · {{ o.eta }}</span>
              </span>
              <VdIcon v-if="o.code === outlet.selectedCode" name="check" :size="18" class="mt-0.5 shrink-0 text-vd-brand" />
            </button>
          </li>
          <li v-if="outlet.outlets.length === 0" class="px-3 py-6 text-center text-sm text-vd-muted">
            Точки доставки пока недоступны
          </li>
        </ul>
      </div>
    </div>
  </header>
</template>
